import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2Icon, PencilIcon, PlusIcon, PowerIcon, SearchIcon } from 'lucide-react'
import { toast } from 'sonner'
import { type CreditType, getCreditTypes, saveCreditType } from '@/lib/credits-api'
import { getErrorMessage } from '@/lib/error-messages'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function AdminCreditTypesPage() {
  const client = useQueryClient()
  const types = useQuery({ queryKey: ['credit-types'], queryFn: getCreditTypes })
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CreditType | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const save = useMutation({
    mutationFn: saveCreditType,
    onSuccess: () => {
      void client.invalidateQueries()
      setDialogOpen(false)
      setEditing(null)
      setName('')
      setCode('')
      toast.success('Credits gespeichert.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const search = searchTerm.trim().toLocaleLowerCase('de-AT')
  const filteredTypes = (types.data ?? []).filter((type) => {
    const matchesSearch = `${type.name} ${type.code}`.toLocaleLowerCase('de-AT').includes(search)
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'active' ? type.is_active : !type.is_active)
    return matchesSearch && matchesStatus
  })

  function openDialog(type: CreditType | null) {
    setEditing(type)
    setCode(type?.code ?? '')
    setName(type?.name ?? '')
    setDialogOpen(true)
  }

  return (
    <div className='min-w-0 space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <h1 className='text-2xl font-semibold'>Credits</h1>
          <p className='text-sm text-muted-foreground'>
            Credits für Kurse und Produktleistungen verwalten.
          </p>
        </div>
        <Button
          type='button'
          className='w-full sm:w-auto'
          disabled={save.isPending}
          onClick={() => openDialog(null)}
        >
          <PlusIcon className='mr-2 h-4 w-4' />
          Credits anlegen
        </Button>
      </div>

      <Card className='lg:flex lg:max-h-[calc(100dvh-12.5rem)] lg:min-h-0 lg:flex-col'>
        <CardHeader>
          <CardTitle className='text-lg'>Credits</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-hidden'>
          <div className='grid shrink-0 gap-3 md:grid-cols-[minmax(0,1fr)_12rem]'>
            <div className='relative'>
              <SearchIcon className='pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                aria-label='Credits suchen'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder='Nach Name oder Kennung suchen ...'
                className='pl-9'
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}
            >
              <SelectTrigger aria-label='Status filtern' className='bg-card'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>Alle Status</SelectItem>
                <SelectItem value='active'>Aktiv</SelectItem>
                <SelectItem value='inactive'>Inaktiv</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <TooltipProvider delayDuration={150}>
            <Table
              aria-label='Credits'
              className='min-w-[540px]'
              containerClassName='max-h-[60dvh] lg:max-h-[calc(100dvh-24rem)]'
            >
              <TableHeader>
                <TableRow>
                  <TableHead scope='col'>Name</TableHead>
                  <TableHead scope='col'>Kennung</TableHead>
                  <TableHead scope='col'>Status</TableHead>
                  <TableHead scope='col' className='text-right'>Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.isPending ? (
                  <TableRow>
                    <TableCell colSpan={4} className='h-24 text-center text-muted-foreground'>
                      Credits werden geladen …
                    </TableCell>
                  </TableRow>
                ) : types.isError ? (
                  <TableRow>
                    <TableCell colSpan={4} className='h-24 text-center'>
                      <p role='alert' className='text-destructive'>Credits konnten nicht geladen werden.</p>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='mt-2'
                        disabled={types.isFetching}
                        onClick={() => void types.refetch()}
                      >
                        Erneut versuchen
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : filteredTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className='h-24 text-center text-muted-foreground'>
                      {types.data.length === 0
                        ? 'Noch keine Credits vorhanden. Lege über „Credits anlegen“ den ersten Eintrag an.'
                        : 'Keine Credits für diese Suche oder diesen Status gefunden.'}
                    </TableCell>
                  </TableRow>
                ) : filteredTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell className='font-medium'>{type.name}</TableCell>
                    <TableCell className='text-muted-foreground'>{type.code}</TableCell>
                    <TableCell>
                      <Badge variant={type.is_active ? 'default' : 'secondary'}>
                        {type.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='flex justify-end gap-2'>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type='button'
                              variant='outline'
                              size='sm'
                              aria-label={`${type.name} ${type.is_active ? 'deaktivieren' : 'aktivieren'}`}
                              disabled={save.isPending}
                              onClick={() => save.mutate({ ...type, is_active: !type.is_active })}
                            >
                              <PowerIcon className='h-4 w-4' />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{type.is_active ? 'Deaktivieren' : 'Aktivieren'}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type='button'
                              variant='outline'
                              size='sm'
                              aria-label={`${type.name} bearbeiten`}
                              disabled={save.isPending}
                              onClick={() => openDialog(type)}
                            >
                              <PencilIcon className='h-4 w-4' />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Bearbeiten</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TooltipProvider>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => !save.isPending && setDialogOpen(open)}>
        <DialogContent className='max-h-[90dvh] w-[calc(100vw-1rem)] overflow-y-auto sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>{editing ? 'Credits bearbeiten' : 'Credits anlegen'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Passe den Anzeigenamen an. Die Kennung bleibt unverändert.'
                : 'Lege Credits mit einer eindeutigen Kennung und einem Anzeigenamen an.'}
            </DialogDescription>
          </DialogHeader>
          <form
            className='space-y-5'
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate({ ...(editing ?? {}), code, name: name.trim() })
            }}
          >
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <Label htmlFor='credit-name'>Anzeigename</Label>
                <Input
                  id='credit-name'
                  required
                  disabled={save.isPending}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='credit-code'>Kennung</Label>
                <Input
                  id='credit-code'
                  required
                  pattern='[a-z][a-z0-9_]*'
                  disabled={editing !== null || save.isPending}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder='z. B. personal_training'
                />
              </div>
            </div>
            <DialogFooter className='gap-2'>
              <Button
                type='button'
                variant='outline'
                disabled={save.isPending}
                onClick={() => setDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button type='submit' disabled={save.isPending || !name.trim()}>
                {save.isPending && <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />}
                {save.isPending ? 'Speichert ...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
